import { useEffect, useState } from 'react';

/**
 * Как часто пересчитывать декорации папок при открытом Диске.
 *
 * Декорации (`defineDriveDecorations`) зависят не только от полей папки, но и
 * от внешнего состояния — прежде всего от текущей даты («эта неделя —
 * текущая»). Вычисление в рендере спасает от устаревшего `useMemo`, но сам
 * рендер в полночь не случится: вкладка, оставленная открытой без активности,
 * показывала бы прошлую неделю.
 *
 * Пять минут — компромисс: задержка подсветки после смены недели незаметна
 * (граница суток верфи — 19:00 по Москве, никто не сверяет её по секундам), а
 * фоновых рендеров 12 в час при десятках узлов дерева. Точный таймер до
 * границы суток был бы экономнее, но требует знать таймзону домена — а
 * платформа про `Asia/Shanghai` знать не должна.
 */
const PULSE_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Пульс перерисовки декораций: возвращает значение, меняющееся со временем и
 * при возвращении пользователя на вкладку.
 *
 * Использовать как ЗАВИСИМОСТЬ (в `useMemo` колонок) или просто вызвать в
 * компоненте, который рисует декорации, — важен сам факт ререндера, а не
 * значение.
 *
 * Возврат на вкладку обработан отдельно от интервала: типовой сценарий —
 * «Диск оставили открытым на ночь», и там правильная подсветка нужна сразу,
 * а не через пять минут. Браузеры к тому же тормозят таймеры в фоне, так что
 * на один интервал полагаться нельзя.
 *
 * @example
 * ```tsx
 * const pulse = useDecorationPulse();
 * const columns = useMemo(() => buildDriveColumns({ … }), [foldersById, pulse]);
 * ```
 */
export function useDecorationPulse(): number {
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const bump = () => setPulse(value => value + 1);
    const timer = setInterval(bump, PULSE_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return pulse;
}
